"use client";
import { mobileLinks, links as navLinks } from "@/data/navbar.data";
import useWindowSize from "@/hooks/useWindowSize";
import React, { useRef, useState } from "react";
import ServicesSubMenu from "./ServicesSubMenu";
import MobileMenu from "./MobileMenu";
import { usePathname } from "next/navigation";
import { TransitionLink } from "../shared/TransitionLink";
import DesktopSubMenu from "./DesktopSubMenu";
import { AnimatePresence, motion } from "framer-motion";
import { fadeUpAnimationData } from "@/data/animations.data";
import { useDict } from "@/components/providers/LocaleProvider";

type LinksTabsProps = {
  navHidden: boolean;
};

const LinksTabs = ({ navHidden }: LinksTabsProps) => {
  const { width, isCompactNav } = useWindowSize();
  const pathname = usePathname();
  const t = useDict();

  const compact = isCompactNav ?? false;
  const tabWidth = compact
    ? width && width < 382
      ? 60
      : width && width < 632
        ? 75
        : 110
    : width && width >= 1280
      ? 110
      : 88;
  const tabGap = compact ? 8 : width && width >= 1280 ? 8 : 6;
  const [currentHoverNo, setCurrentHoverNo] = useState<null | number>(null);
  const [isSelected, setIsSelected] = useState(0);
  const [isSubmenuHover, setIsSubmenuHover] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startCloseTimer = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(null), 100);
  };
  const cancelCloseTimer = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const links = navLinks.filter((l) => compact || !l.onlyMobile);
  const SelectedLink =
    links[currentHoverNo !== null ? currentHoverNo : isSelected];

  const isServicesOpen =
    ((currentHoverNo !== null &&
      links?.[currentHoverNo]?.id === "services") ||
      isSubmenuHover) &&
    !compact;

  const showDock = compact || !navHidden;

  return (
    <AnimatePresence>
      {showDock && (
        <motion.section
          {...fadeUpAnimationData}
          onMouseLeave={() => {
            if (!isSubmenuHover && !isServicesOpen) setCurrentHoverNo(null);
          }}
          className="relative flex justify-center items-center w-fit gap-x-2 lg:gap-x-1.5 xl:gap-x-2 rounded-full bg-dark-gray p-2 lg:p-1.5 xl:p-2 border border-light-border"
        >
          {(compact ? mobileLinks.slice(0, 3) : links).map((link, idx) => {
            const label = t.nav.links[link.id];
            if (link.type === "link") {
              return (
                <TransitionLink
                  title={label}
                  href={link.href}
                  key={link.id}
                  aria-label={label}
                  onMouseEnter={() => {
                    setCurrentHoverNo(idx);
                    cancelCloseTimer();
                    setOpen(link.id);
                    if (link.id !== "services") setIsSubmenuHover(false);
                  }}
                  onMouseLeave={startCloseTimer}
                  onClick={() => setIsSelected(idx)}
                  className={`py-2 lg:py-1.5 xl:py-2 flex flex-col justify-center items-center cursor-pointer hover:text-black gap-[2px] rounded-full transition-colors duration-300 z-10 ${
                    pathname === link.href
                      ? "bg-white text-black rounded-full"
                      : ""
                  }  ${link.className}`}
                  style={{ width: `${tabWidth}px`, textAlign: "center" }}
                >
                  {<link.Icon className="lg:hidden" size={link.iconSize} />}
                  <span className="text-[10px] lg:text-base">{label}</span>
                </TransitionLink>
              );
            }

            return (
              <DesktopSubMenu
                key={link.id}
                entry={link}
                open={open}
                setOpen={setOpen}
                startCloseTimer={startCloseTimer}
                cancelCloseTimer={cancelCloseTimer}
                style={{ width: `${tabWidth}px`, textAlign: "center" }}
                buttonProps={{
                  onMouseEnter: () => {
                    setCurrentHoverNo(idx);
                  },
                }}
              />
            );
          })}

          <MobileMenu width={`${tabWidth}px`} />

          <ServicesSubMenu
            isOpen={isServicesOpen}
            onMouseEnter={() => setIsSubmenuHover(true)}
            onMouseLeave={() => {
              setIsSubmenuHover(false);
              if (
                currentHoverNo === null ||
                links?.[currentHoverNo]?.id !== "services" ||
                currentHoverNo !== null
              ) {
                setCurrentHoverNo(null);
              }
            }}
          />

          {currentHoverNo !== null && (
            <div
              className={`absolute top-2 lg:top-1.5 xl:top-2 max-lg:hidden pointer-events-none bottom-2 lg:bottom-1.5 xl:bottom-2 z-30 flex justify-center items-center rounded-full transition-all duration-300 ease-in-out  ${
                isSelected === currentHoverNo || currentHoverNo === null
                  ? "bg-white text-black"
                  : "bg-gray-700"
              }`}
              style={{
                width: `${tabWidth}px`,
                left: `${
                  currentHoverNo * tabWidth + tabGap + tabGap * currentHoverNo
                }px`,
              }}
            >
              <span className="flex gap-[2px] flex-col items-center justify-center h-full text-[10px] lg:text-base">
                <SelectedLink.Icon
                  className="lg:hidden"
                  size={SelectedLink.iconSize}
                />
                {t.nav.links[SelectedLink.id]}
              </span>
            </div>
          )}
        </motion.section>
      )}
    </AnimatePresence>
  );
};

export default LinksTabs;
